/**
 * Wrap lexicon `number` fields in CborFloat before DAG-CBOR signing (F7).
 * Paths come from the generated FLOAT_PATHS manifest (one source of truth =
 * the lexicon). A path segment "[]" means "every array element".
 */
import { CborFloat, type CborValue } from "@beacon/crypto";
import { FLOAT_PATHS } from "@beacon/lexicon";

function setAtPath(node: unknown, segs: readonly string[]): void {
  if (node === null || typeof node !== "object") return;
  const [head, ...rest] = segs;
  if (head === "[]") {
    if (Array.isArray(node)) {
      if (rest.length === 0) {
        for (let i = 0; i < node.length; i++) {
          if (typeof node[i] === "number") node[i] = new CborFloat(node[i] as number);
        }
      } else {
        for (const el of node) setAtPath(el, rest);
      }
    }
    return;
  }
  const obj = node as Record<string, unknown>;
  if (!(head in obj)) return; // optional field absent
  if (rest.length === 0) {
    if (typeof obj[head] === "number") obj[head] = new CborFloat(obj[head] as number);
  } else {
    setAtPath(obj[head], rest);
  }
}

/** Returns a copy of the CBOR model with number fields (per FLOAT_PATHS) boxed. */
export function boxFloats(model: Record<string, CborValue>, $type: string): Record<string, CborValue> {
  const paths = FLOAT_PATHS[$type];
  if (!paths || paths.length === 0) return model;
  const copy = structuredClone(model);
  for (const p of paths) setAtPath(copy, p.split("."));
  return copy as Record<string, CborValue>;
}
